/**
 * Canonical localStorage keys for the auth session and the sysadmin emulation
 * session.
 *
 * Anything that reads or writes a session — the auth provider, the axios
 * interceptors, the setup wizard, the invitation join flow, an export hook that
 * needs a bearer token for a raw `fetch` — MUST use these constants. A
 * hand-typed key silently stores a token nothing reads, leaving the app
 * unauthenticated on the next bootstrap (the WI 06ca8f95 production bug).
 *
 * This module is a **leaf**: it imports nothing, so importing it directly is
 * always safe and is the default. Anything upstream of `AuthContext` —
 * `axiosInstance`, `EmulationContext` — MUST import it directly, since the
 * `shared/auth` barrel pulls in `AuthProvider` and would close a cycle. The
 * barrel re-exports these keys too (same object, not a copy), so a file that
 * already imports from `shared/auth` can take them from there instead of
 * adding a second import.
 *
 * `tools/check_auth_storage_keys.py` fails the build if the raw literals
 * reappear anywhere else under `apps/frontend/src`.
 */

/** The sysadmin-emulation session, written when a sysadmin emulates a user. */
export const EMULATION_STORAGE_KEYS = {
  session: 'panluma_emulation_session',
  token: 'panluma_emulation_token',
  originalToken: 'panluma_original_token',
  originalUser: 'panluma_original_user',
  originalLanguage: 'panluma_original_language',
} as const;

export const AUTH_STORAGE_KEYS = {
  accessToken: 'panluma_access_token',
  refreshToken: 'panluma_refresh_token',
  user: 'panluma_user',
  /** Present iff a sysadmin emulation is active — same key as `EMULATION_STORAGE_KEYS.token`. */
  emulationToken: EMULATION_STORAGE_KEYS.token,
} as const;
